import * as React from "react";
import * as moment from "moment";
import {getAmountToDisplay, getNextInstalmentDate, isCancellationProcess} from "../../CommonFunctions";
import {Link} from "react-router-dom";
import {
    AnnualPolicyInfo, AnnualPolicyPremiumTypeEnum, InstalmentsInfo, ProductsEnum,
    D_MMM_YYYY_DATE_FORMAT,
    INTERACTION_PROCESS_MTC_LV,
    PREMIUM_TYPE_MONTHLY
} from "@falcon/services";
import {WarningBanner} from "./WarningBanner";
import {NextPaymentBanner} from "./NextPaymentBanner";

interface IProps {
    showInfoModal?: boolean;
    interaction: any;
    showMissedPaymentBanner: boolean;
    instalmentsInfo?: InstalmentsInfo;
    hasRenewalDeclined?: boolean;
    policyInfo?: AnnualPolicyInfo;
    amount?: number;
    ncdImpactedClaim?: boolean;
    reuseCard?: boolean
}

export class AnnualProductPaymentMessageBanner extends React.Component<IProps> {

    private static readonly CANCELLATION_REASON_CODE_MISSED_PAYMENT: string = "MISSED_PAYMENT";
    private static readonly CANCELLATION_REASON_CODE_LAPSED: string = "LAPSED";
    private static readonly MONTHLY_PAYMENT_DAYS_UNTIL_RETRY: number = 7;
    private static readonly MONTHLY_PAYMENT_DAYS_UNTIL_CANCELLATION: number = 14;
    private static readonly ANNUAL_RENEWAL_PAYMENT_DAYS_UNTIL_RETRY: number = 7;
    private static readonly ANNUAL_RENEWAL_PAYMENT_DAYS_UNTIL_CANCELLATION: number = 14;

    private static createDate(daysToAdd: number, paymentDate: moment.Moment): moment.Moment {
        return moment(paymentDate).add(daysToAdd, "day");
    }

    public componentDidMount() {
        const endDate: any = moment.utc(this.props.interaction.headerInfo.endDate, moment.ISO_8601)

        if (endDate.isBefore(moment(), 'day')) {
            window.sessionStorage.setItem("bannerCalledInSession" + this.props.interaction.agreementNumber, "true");
            window.localStorage.setItem("bannerCalledLocally" + this.props.interaction.agreementNumber, "true");
        }
    }

    public render() {
        const {interaction, showInfoModal, showMissedPaymentBanner, instalmentsInfo, hasRenewalDeclined, policyInfo, ncdImpactedClaim} = this.props;

        const premiumToCompare: string = this.getPremiumToCompare(interaction);
        const isFullRefundProvided: boolean = this.isFullRefundProvided(interaction.factors.returnPremium, premiumToCompare);
        const hasInstalments = instalmentsInfo && instalmentsInfo.instalments && instalmentsInfo.instalments.length > 0;

        if (this.showCancellationBanner(interaction, isFullRefundProvided, hasRenewalDeclined, ncdImpactedClaim)) {
            const endDate: any = moment.utc(interaction.headerInfo.endDate, moment.ISO_8601)

            if (this.showMissedPaymentBannerForCancellation(interaction, showMissedPaymentBanner) && hasInstalments) {

                let paymentDate: moment.Moment;
                let secondPaymentAttemptDate: moment.Moment;
                paymentDate = getNextInstalmentDate(instalmentsInfo);
                secondPaymentAttemptDate = AnnualProductPaymentMessageBanner.createDate(AnnualProductPaymentMessageBanner.MONTHLY_PAYMENT_DAYS_UNTIL_RETRY, paymentDate);
                return this.getPolicyCanceledContentWithMissedPayment(endDate, secondPaymentAttemptDate)
            } else {
                return this.getPolicyCanceledContent(endDate, interaction.product);
            }
        }

        if (this.showPaymentBannerForMonthlyPremium(interaction, isFullRefundProvided) && hasInstalments) {
            const {
                paymentDate,
                secondPaymentAttemptDate,
                cancellationDate,
                isCancelledAutomatically
            } = this.getPaymentDates(interaction, policyInfo, instalmentsInfo, false);

            return (
                <>
                    {!showMissedPaymentBanner &&
                        <NextPaymentBanner showInfoModal={showInfoModal!} paymentDate={paymentDate}
                                           instalmentsInfo={instalmentsInfo}/>}
                    {showMissedPaymentBanner && this.getMissedPaymentContent(secondPaymentAttemptDate, cancellationDate, paymentDate, isCancelledAutomatically, false, ncdImpactedClaim)}
                </>
            );
        }

        if (this.showPaymentBannerForAnnualPremium(interaction) && hasInstalments ) {
            const {
                paymentDate,
                secondPaymentAttemptDate,
                cancellationDate,
                isCancelledAutomatically
            } = this.getPaymentDates(interaction, policyInfo, instalmentsInfo, true);

            return (
                <>
                    {showMissedPaymentBanner && this.getMissedPaymentContent(secondPaymentAttemptDate, cancellationDate, paymentDate, isCancelledAutomatically, true, ncdImpactedClaim)}
                </>
            );
        }

        if (interaction.process === INTERACTION_PROCESS_MTC_LV) {
            const {
                paymentDate,
                secondPaymentAttemptDate,
                cancellationDate
            } = this.getPaymentDates(interaction, policyInfo, instalmentsInfo, false);

            return (
                <>
                    {showMissedPaymentBanner && this.getMissedPaymentContent(secondPaymentAttemptDate, cancellationDate, paymentDate, true, interaction.factors.premiumType === AnnualPolicyPremiumTypeEnum.ANNUAL, ncdImpactedClaim)}
                </>
            );
        }

        return <></>
    }

    private getPremiumToCompare(interaction: any): string {
        return interaction.factors.premiumType === PREMIUM_TYPE_MONTHLY
            ? interaction.paymentDetails.instalmentPlan.deposit
            : interaction.factors.annualPremium;
    }

    private showCancellationBanner(interaction: any, isFullRefundProvided: boolean, hasRenewalDeclined?: boolean, ncdImpactedClaim?: boolean): boolean {
        return isCancellationProcess(interaction.process)
            && !isFullRefundProvided
            && !hasRenewalDeclined
            && !ncdImpactedClaim
            && AnnualProductPaymentMessageBanner.CANCELLATION_REASON_CODE_MISSED_PAYMENT !== interaction.cancellationReasonInfo?.cancellationReasonCode
    }

    private showMissedPaymentBannerForCancellation(interaction: any, showMissedPaymentBanner: boolean): boolean {
        return interaction.factors.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY
            && showMissedPaymentBanner
            && AnnualProductPaymentMessageBanner.CANCELLATION_REASON_CODE_LAPSED === interaction.cancellationReasonInfo?.cancellationReasonCode
    }

    private showPaymentBannerForMonthlyPremium(interaction: any, isFullRefundProvided: boolean): boolean {
        return interaction.factors.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY
            && !isFullRefundProvided
    }

    private showPaymentBannerForAnnualPremium(interaction: any) {
        return interaction.factors.premiumType === AnnualPolicyPremiumTypeEnum.ANNUAL
            && interaction.renewalDetails?.policyAgeYears > 0
    }

    private getPaymentDates(interaction: any, policyInfo: any, instalmentsInfo: any, isAnnualPayment: boolean) {
        const isCancelledAutomatically = interaction.process === INTERACTION_PROCESS_MTC_LV;
        let paymentDate: moment.Moment;
        let secondPaymentAttemptDate: moment.Moment;
        let cancellationDate: moment.Moment;

        if (isCancelledAutomatically) {
            cancellationDate = moment(interaction.headerInfo.endDate).endOf('day');
            paymentDate = policyInfo?.delinquencyStartDate
                ? moment(policyInfo?.delinquencyStartDate).endOf('day')
                : moment(interaction.headerInfo.endDate)
                    .subtract(this.getPaymentDaysUntilCancellation(isAnnualPayment), 'day')
                    .startOf('day');
            secondPaymentAttemptDate = moment(interaction.headerInfo.endDate)
                .subtract(isAnnualPayment ? AnnualProductPaymentMessageBanner.ANNUAL_RENEWAL_PAYMENT_DAYS_UNTIL_RETRY : AnnualProductPaymentMessageBanner.MONTHLY_PAYMENT_DAYS_UNTIL_RETRY, 'day')
                .startOf('day');
        } else {
            paymentDate = getNextInstalmentDate(instalmentsInfo);
            secondPaymentAttemptDate = AnnualProductPaymentMessageBanner.createDate(
                isAnnualPayment ? AnnualProductPaymentMessageBanner.ANNUAL_RENEWAL_PAYMENT_DAYS_UNTIL_RETRY : AnnualProductPaymentMessageBanner.MONTHLY_PAYMENT_DAYS_UNTIL_RETRY,
                paymentDate
            );
            cancellationDate = AnnualProductPaymentMessageBanner.createDate(
                isAnnualPayment ? AnnualProductPaymentMessageBanner.ANNUAL_RENEWAL_PAYMENT_DAYS_UNTIL_CANCELLATION : AnnualProductPaymentMessageBanner.MONTHLY_PAYMENT_DAYS_UNTIL_CANCELLATION,
                paymentDate
            ).endOf('day');
        }

        if (interaction?.uistate?.cancellationDelay) {
            cancellationDate = moment(interaction.uistate.cancellationDelay).endOf('day');
            paymentDate = policyInfo?.delinquencyStartDate
                ? moment(policyInfo?.delinquencyStartDate).endOf('day')
                : moment(interaction.uistate.oldestUnpaidInvoiceDueDate).endOf('day');
        }

        return { paymentDate, secondPaymentAttemptDate, cancellationDate, isCancelledAutomatically };
    }

    private getPaymentDaysUntilCancellation(isAnnual: boolean) {
        return isAnnual ? AnnualProductPaymentMessageBanner.ANNUAL_RENEWAL_PAYMENT_DAYS_UNTIL_CANCELLATION
            : AnnualProductPaymentMessageBanner.MONTHLY_PAYMENT_DAYS_UNTIL_CANCELLATION
    }

    private isFullRefundProvided (interactionReturnPremium: string, comparedValue: string): boolean {
        return parseFloat(interactionReturnPremium) === parseFloat(comparedValue) && parseFloat(interactionReturnPremium) !== 0;
    }

    private getPolicyCanceledContent(endDate: moment.Moment, product: ProductsEnum) {
        let wording: string;

        if (endDate.isBefore(moment(), 'day')) {
            wording = `Your ${product === ProductsEnum.HOME ? "home" : "car"} policy was cancelled on ${endDate.format(D_MMM_YYYY_DATE_FORMAT)}, 23:59.`;
        } else {
            wording = `Your ${product === ProductsEnum.HOME ? "home" : "car"}  policy will end on ${endDate.format(D_MMM_YYYY_DATE_FORMAT)}, 23:59. Until then you are still covered`;
        }

        return (
            <WarningBanner wording={wording}/>
        );
    }

    private getPolicyCanceledContentWithMissedPayment(endDate: moment.Moment, secondPaymentAttemptDate: moment.Moment) {
        let content: JSX.Element;
        const formattedSecondPaymentAttemptDate:string = secondPaymentAttemptDate.format(D_MMM_YYYY_DATE_FORMAT);
        const formattedCancellationDate:string = endDate.format(D_MMM_YYYY_DATE_FORMAT);
        const now: moment.Moment = moment().utc();

        if (endDate.isBefore(now, 'day')) {
            content =
                <div className="banner-text" id={"firstMissedPayment"}>
                    <p>We didn't receive payment for your Allianz policy this month.{' '}
                        The policy has now expired on the{' '}
                        <span className="font-weight-bold">{formattedCancellationDate}, 23:59</span>.
                        You still need to pay that last payment.
                        You can {this.linkForPaymentAfterPolicyExpireDate(
                            secondPaymentAttemptDate,
                            now,
                            formattedSecondPaymentAttemptDate)}
                    </p>
                    {this.payNowLink()}
                </div>;
        } else {
            content =
                <div className="banner-text" id={"firstMissedPayment"}>
                    <p>We didn't receive payment for your Allianz policy this month.{' '}
                        {this.linkForPaymentBeforePolicyExpireDate(
                            secondPaymentAttemptDate,
                            now,
                            formattedSecondPaymentAttemptDate,
                            formattedCancellationDate)}
                    </p>
                    {this.payNowLink()}
                </div>;
        }

        return (<div>
            {content}
        </div>);
    }

    private linkForPaymentAfterPolicyExpireDate(secondPaymentAttemptDate: moment.Moment,
                                                now: moment.Moment,
                                                formattedSecondPaymentAttemptDate: string) {
        if (now.isAfter(secondPaymentAttemptDate, 'day')) {
            return (
                <Link id={"payNow-inline"}
                      className="text-decoration-underline cursor-pointer muli-bold-16"
                      to={{
                          pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`,
                          state: {verifyCardFailed: false, product: ProductsEnum.WOLF_MOTOR}
                      }}>pay now.</Link>
            )
        } else {
            return <>
                <Link id={"payNow-inline"}
                      className="text-decoration-underline cursor-pointer muli-bold-16"
                      to={{
                          pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`,
                          state: {verifyCardFailed: false, product: ProductsEnum.WOLF_MOTOR}
                      }}>pay now, </Link>
                or we'll try the payment again{' '}
                on the <span className="font-weight-bold">{formattedSecondPaymentAttemptDate}</span>.{' '}
            </>;
        }
    }

    private payNowLink(){
        return (
            <Link id={"payNow"} className="text-decoration-underline cursor-pointer muli-bold-16"
                  to={{
                      pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`,
                      state: {verifyCardFailed: false, product: ProductsEnum.WOLF_MOTOR}
                  }}>Pay now</Link>
        )
    }

    private linkForPaymentBeforePolicyExpireDate(secondPaymentAttemptDate: moment.Moment,
                                                 now: moment.Moment,
                                                 formattedSecondPaymentAttemptDate: string,
                                                 formattedCancellationDate: string) {
        if (now.isAfter(secondPaymentAttemptDate, 'day')) {
            return <>
                The policy will expire on the{' '}
                <span className="font-weight-bold">{formattedCancellationDate} and 23:59</span>.
                You still need to pay that last payment.
                You can <Link id={"payNow-inline"} className="text-decoration-underline cursor-pointer muli-bold-16"
                              to={{
                                  pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`,
                                  state: {verifyCardFailed: false, product: ProductsEnum.WOLF_MOTOR}
                              }}>pay now.</Link>
            </>;
        } else {
            return <>
                <Link id={"payNow-inline"} className="text-decoration-underline cursor-pointer muli-bold-16"
                      to={{
                          pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`,
                          state: {verifyCardFailed: false, product: ProductsEnum.WOLF_MOTOR}
                      }}>Pay now, </Link>
                or we'll try the payment again{' '}
                on the <span className="font-weight-bold">{formattedSecondPaymentAttemptDate}</span>.{' '}
            </>;
        }
    }

    private getMissedPaymentContent(secondPaymentAttemptDate: moment.Moment,
                                    cancellationDate: moment.Moment,
                                    paymentDate: moment.Moment,
                                    isCancelledAutomatically: boolean,
                                    isAnnual: boolean,
                                    ncdImpactedClaim?: boolean): JSX.Element {
        const now: moment.Moment = moment().utc();
        const formattedPaymentDate:string  = paymentDate.format(D_MMM_YYYY_DATE_FORMAT);
        const formattedSecondPaymentAttemptDate:string = secondPaymentAttemptDate.format(D_MMM_YYYY_DATE_FORMAT);
        const formattedCancellationDate:string = cancellationDate.format(D_MMM_YYYY_DATE_FORMAT);
        const policyProduct: ProductsEnum = this.props.interaction.product;

        if (isCancelledAutomatically || now.isAfter(moment(cancellationDate))) {
            return (
                <div className="banner dashboard-missed-payment-banner">
                    <div className="banner-text" id={"cancellationDate"}>
                        We've been in touch with you number of times since <span
                        className="font-weight-bold">{formattedPaymentDate}</span>{' '}
                        because we didn't receive your payment.{' '}
                        Your cover ended on <span
                        className="font-weight-bold">{formattedCancellationDate}, 23:59.</span>
                    </div>
                </div>);
        } else if (now.isSameOrAfter(moment(secondPaymentAttemptDate)) && this.props.amount) {
            return this.getSecondMissedPaymentContent(policyProduct, formattedCancellationDate, isAnnual, ncdImpactedClaim);
        } else if (now.isSameOrAfter(moment(paymentDate)) && this.props.amount) {
            return this.getFirstMissedPaymentContent(formattedSecondPaymentAttemptDate, formattedCancellationDate, isAnnual, ncdImpactedClaim);
        }
        return (<></>);
    }

    private getFirstMissedPaymentContent(formattedSecondPaymentAttemptDate: string,
                                         formattedCancellationDate: string,
                                         isAnnual: boolean,
                                         ncdImpactedClaim?: boolean): React.JSX.Element {
        if (ncdImpactedClaim) {
            return <div className="banner dashboard-missed-payment-banner">
                    <div className="banner-text" id={"firstMissedPayment"}>
                        <p className="font-weight-bold">We didn't receive payment for your Allianz {isAnnual ? "renewal" : "policy this month"}.</p>
                        <p>You can <Link id={"payNow-inline"} className="text-decoration-underline cursor-pointer muli-bold-16"
                                      to={{
                                          pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`,
                                          state: {verifyCardFailed: false, product: ProductsEnum.WOLF_MOTOR}
                                      }}>{'pay ' + getAmountToDisplay(this.props.amount) + ' now, '}</Link>
                        or we'll try the payment again{' '}
                        on the <span className="font-weight-bold">{formattedSecondPaymentAttemptDate}</span>.{' '}
                        If payment on the <span className="font-weight-bold">{formattedSecondPaymentAttemptDate}</span> is
                        unsuccessful and you don't pay by {' '}
                        <span className="font-weight-bold">{formattedCancellationDate}</span> we may pass it to a debt collection
                            agency and this may impact your credit rating.
                        <Link id={"payNow"} className="text-decoration-underline cursor-pointer muli-bold-16"
                              to={{
                                  pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`,
                                  state: {verifyCardFailed: false, product: ProductsEnum.WOLF_MOTOR}
                              }}>{'Pay ' + getAmountToDisplay(this.props.amount) + ' now'}</Link>
                        </p>
                    </div>
                </div>
        } else {
            return <div className="banner dashboard-missed-payment-banner">
                <div className="banner-text" id={"firstMissedPayment"}>
                    <p>We didn't receive payment for your Allianz {isAnnual ? "renewal" : "policy this month"}.{' '}
                        You can <Link id={"payNow-inline"}
                                      className="text-decoration-underline cursor-pointer muli-bold-16"
                                      to={{
                                          pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`,
                                          state: {verifyCardFailed: false, product: ProductsEnum.WOLF_MOTOR}
                                      }}>{'pay ' + getAmountToDisplay(this.props.amount) + ' now, '}</Link>
                        or we'll try the payment again{' '}
                        on the <span className="font-weight-bold">{formattedSecondPaymentAttemptDate}</span>.{' '}
                        If payment on the <span
                            className="font-weight-bold">{formattedSecondPaymentAttemptDate}</span> is
                        unsuccessful,
                        we'll cancel your cover on{' '}
                        <span className="font-weight-bold">{formattedCancellationDate}, 23:59</span> (as per your terms
                        and
                        conditions).
                        <Link id={"payNow"} className="text-decoration-underline cursor-pointer muli-bold-16"
                              to={{
                                  pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`,
                                  state: {verifyCardFailed: false, product: ProductsEnum.WOLF_MOTOR}
                              }}>{'Pay ' + getAmountToDisplay(this.props.amount) + ' now'}</Link>
                    </p>
                </div>
            </div>
        }
    }

    private getSecondMissedPaymentContent(policyProduct: ProductsEnum,
                                          formattedCancellationDate: string,
                                          isAnnual: boolean,
                                          ncdImpactedClaim?: boolean): React.JSX.Element {
        if (ncdImpactedClaim) {
            return <div className="banner dashboard-missed-payment-banner">
                <div className="banner-text" id={"secondMissedPayment"}>
                    <p className="font-weight-bold">We didn't receive payment for your Allianz {isAnnual ? "renewal" : "policy this month"}.</p>
                    <p>You can <Link id={"payNow-inline"} className="text-decoration-underline cursor-pointer muli-bold-16"
                              to={{pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`, state: { verifyCardFailed: false, product: policyProduct }}}>{'Pay ' + getAmountToDisplay(this.props.amount) + ' now. '}</Link>
                        If you don't pay by {' '}
                        <span className="font-weight-bold">{formattedCancellationDate}</span> we may pass it to a debt collection agency
                        and this may impact your credit rating.
                    </p>
                    <Link id={"payNow"} className="text-decoration-underline cursor-pointer muli-bold-16"
                          to={{pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`, state: { verifyCardFailed: false, product: policyProduct }}}>{'Pay ' + getAmountToDisplay(this.props.amount) + ' now'}</Link>
                </div>
            </div>
        } else {
            return <div className="banner dashboard-missed-payment-banner">
                <div className="banner-text" id={"secondMissedPayment"}>
                    <p>We didn't receive payment for your
                        Allianz {isAnnual ? "renewal" : "policy this month"}.{' '}
                        <Link id={"payNow-inline"} className="text-decoration-underline cursor-pointer muli-bold-16"
                              to={{pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`, state: { verifyCardFailed: false, product: policyProduct }}}>{'Pay ' + getAmountToDisplay(this.props.amount) + ' now, '}</Link>
                        or we'll cancel your Allianz policy on the{' '}
                        <span className="font-weight-bold">{formattedCancellationDate}, 23:59</span> (as per your terms and conditions).
                    </p>
                    <Link id={"payNow"} className="text-decoration-underline cursor-pointer muli-bold-16"
                          to={{pathname: `/dashboard/account/${this.props.reuseCard ? "payOverdueInstalment" : "missedPolicyPayment"}/${this.props.interaction.agreementNumber}`, state: { verifyCardFailed: false, product: policyProduct }}}>{'Pay ' + getAmountToDisplay(this.props.amount) + ' now'}</Link>
                </div>
            </div>
        }

    }
}



// WEBPACK FOOTER //
// ./src/components/banners/AnnualProductPaymentMessageBanner.tsx